import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { CENTERED_ICON, COLORS, InterestFreePill, MaterialIcon, type MaterialIconType, RADIUS } from '@repo/ui';
import { formatter } from '@repo/utils';

type InstallmentNote = { icon: MaterialIconType; text: string };

/** First-payment lines shown under the monthly amount: upfront plans start today and are paid by debit card. */
export const installmentNotes = (planType?: string, firstDueDate?: string): InstallmentNote[] =>
  planType === 'UPFRONT_PAYMENT'
    ? [
        { icon: 'calendar_month', text: 'Pagás la primera cuota hoy' },
        { icon: 'credit_card', text: 'Pagás con tarjeta de débito' },
      ]
    : firstDueDate
      ? [{ icon: 'calendar_month', text: `Pagás la primera cuota el ${formatter(firstDueDate, 'date')}` }]
      : [];

/** Card shell for the installment plan, same surface as OrderCard. */
export const InstallmentCard = ({ children }: { children: React.ReactNode }) => (
  <Stack
    sx={{
      width: '100%',
      p: 2.5,
      borderRadius: `${RADIUS.xl}px`,
      backgroundColor: COLORS.surface.card,
    }}
  >
    {children}
  </Stack>
);

/** Card header: dark semibold caption plus an optional round icon action on the right. */
export const InstallmentHeader = ({
  label,
  action,
}: {
  label: string;
  action?: { icon: 'edit' | 'arrow_back'; title: string; onClick: () => void };
}) => (
  <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 36 }}>
    <Typography component="p" variant="body1" sx={{ fontWeight: 600, color: COLORS.text.main }}>
      {label}
    </Typography>
    {action && (
      <Tooltip title={action.title}>
        <IconButton
          aria-label={action.title}
          onClick={action.onClick}
          sx={{ width: 36, height: 36, backgroundColor: COLORS.surface.raised }}
        >
          <MaterialIcon icon={action.icon} size={20} sx={{ ...CENTERED_ICON, color: COLORS.text.brand }} />
        </IconButton>
      </Tooltip>
    )}
  </Stack>
);

/** Selected plan: count with an interest-free pill, then the monthly amount with smaller cents. */
export const InstallmentSummary = ({
  label,
  count,
  amount,
  withInterest,
  onEdit,
  notes,
}: {
  label: string;
  count?: number;
  amount?: number;
  withInterest?: boolean;
  onEdit?: () => void;
  notes?: InstallmentNote[];
}) => {
  const [integer, cents] = formatter(amount?.toString(), 'currency').split(',');

  return (
    <Stack sx={{ gap: 1 }}>
      <InstallmentHeader
        label={label}
        action={onEdit ? { icon: 'edit', title: 'Editar cuotas', onClick: onEdit } : undefined}
      />
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography
          component="span"
          sx={{
            typography: 'h2',
            letterSpacing: '0.01em',
            fontSize: 24,
            lineHeight: '30px',
            fontWeight: 700,
            color: COLORS.text.brand,
          }}
        >
          {count} {count === 1 ? 'cuota' : 'cuotas'}
        </Typography>
        {withInterest === false && <InterestFreePill />}
      </Stack>
      <Box sx={{ height: '1px', my: 1, backgroundColor: COLORS.border.brand }} />
      <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start' }}>
          <Typography
            component="span"
            sx={{
              typography: 'h2',
              letterSpacing: '0.01em',
              fontSize: 32,
              lineHeight: '40px',
              fontWeight: 700,
              color: COLORS.text.brand,
            }}
          >
            {integer}
          </Typography>
          {cents && (
            <Typography
              component="span"
              sx={{
                typography: 'h2',
                letterSpacing: '0.01em',
                fontSize: 18,
                lineHeight: '26px',
                fontWeight: 700,
                color: COLORS.text.brand,
              }}
            >
              ,{cents}
            </Typography>
          )}
        </Stack>
        <Typography component="span" variant="subtitle2">
          por mes
        </Typography>
      </Stack>
      {!!notes?.length && (
        <Stack component="ul" sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, gap: 1 }}>
          {notes.map(({ icon, text }) => (
            <Stack component="li" key={text} direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <MaterialIcon icon={icon} size={18} sx={{ ...CENTERED_ICON, color: COLORS.text.brand }} />
              <Typography component="span" variant="subtitle2" sx={{ color: COLORS.text.main }}>
                {text}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
